import {
  Button,
  Email,
  MarketingTerms,
  UnsubscribeTerms,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { UnsubscribeSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const Unsubscribe = ({ content, tracking, brand, query }) => {
  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    email: '',
    response_code: 'RC17171',
    campaign_response_code: '',
    campaign_code: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(UnsubscribeSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (query?.email) {
      methods.setValue('email', query.email)
    }
  }, [query])

  const onSubmit = async (data) => {
    const formData = data
    formData.response_code = brand === 'lexus' ? 'RC101067' : 'RC17171'
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/unsubscribe`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          formData.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav title={content.title} />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          step={1}
          active={true}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <Email />
              <MarketingTerms />
              <UnsubscribeTerms />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'unsubscribe'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
      content: content.attributes,
    },
  }
}

export default Unsubscribe
